import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import './ServiceModal.css';

interface ServiceData {
  title: string;
  description: string;
  type: string;
  image: string;
  images: string[];
  features: string[];
  content: string;
}

interface ServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: ServiceData | null;
}

const ServiceModal: React.FC<ServiceModalProps> = ({
  isOpen,
  onClose,
  service
}) => {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState(0);

  if (!service) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="service-modal-overlay"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3, type: 'spring', damping: 25 }}
              className="service-modal-container"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="service-modal-content">
                <button className="service-modal-close" onClick={onClose} aria-label="Close">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>

                <div className="service-modal-image-section">
                  <img 
                    src={service.images[selectedImage] || service.image} 
                    alt={service.title} 
                    className="service-modal-image" 
                  />
                  {service.images && service.images.length > 1 && (
                    <div className="service-thumbnail-gallery">
                      {service.images.map((img: string, index: number) => (
                        <div
                          key={index}
                          className={`service-thumbnail ${selectedImage === index ? 'active' : ''}`}
                          onClick={() => setSelectedImage(index)}
                        >
                          <img src={img} alt={`${service.title} - ${index + 1}`} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="service-modal-info-section">
                  <h2 className="service-modal-title">{service.title}</h2>
                  
                  {service.type && (
                    <div className="service-type-badge">{service.type}</div>
                  )}

                  <p className="service-modal-description">{service.description}</p>

                  {service.features && service.features.length > 0 && (
                    <div className="service-specs-section">
                      <h3 className="service-specs-title">Özellikler</h3>
                      <div className="service-features-grid">
                        {service.features.map((feature: string, index: number) => (
                          <div key={index} className="service-feature-item">
                            <div className="service-feature-icon">✓</div>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="service-specs-section">
                    <h3 className="service-specs-title">Detaylı Bilgi</h3>
                    <div 
                      className="service-content-html"
                      dangerouslySetInnerHTML={{ __html: service.content }}
                    />
                  </div>

                  <div className="service-modal-actions">
                    <a
                      href={`https://wa.me/905408072727?text=Merhaba,%20${encodeURIComponent(service.title)}%20hizmeti%20hakkında%20bilgi%20almak%20istiyorum.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="service-modal-quote-button"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('chairs.modal.getQuote') || 'Teklif Al'}
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ServiceModal;
